import { Button, Col, DatePicker, Form, Input, message, Row, Select } from 'antd';
import { FormComponentProps } from 'antd/lib/form';
import * as React from 'react';
import {
  ORDER_STATUS,
  CATEGORY_ORIGINAL,
  CATEGORY_PRESCRIPTION,
  orderStateToPayStatus,
  ORDER_PAYMENT,
  PAYMENT_ALIPY,
  PAYMENT_ALIPAY_ORG,
  PAYMENT_WECHAT,
  PAYMENT_WECHAT_ORG,
} from 'src/constants/Order';
import { IPageResult } from 'src/entity/BaseResult';
import { IDrugStoreInfo } from 'src/entity/DrugStoreEntity';
import { IOrderListInfo, IOrderSearchParam } from 'src/entity/OrderEntity';
import { IOrderSearch } from 'src/entity/SearchEntity';
import I18N from 'src/services/I18NService';
import OrderService, { OrderPayment, InvoiceStatusList } from 'src/services/OrderService';
import { MallService } from 'src/services/MallService';
import { ILogistic } from 'src/entity/logistics';

const { RangePicker } = DatePicker;

interface IOrderSearchFormProps extends FormComponentProps {
  isAgent: boolean;
  // category: ORDER_CATEGORY;
  drugStores: IDrugStoreInfo[];
  pageSize: number;
  status: ORDER_STATUS;
  onRef(obj: any): void;
  cbSearch(status: ORDER_STATUS, obj: IPageResult<IOrderListInfo[]>): void;
}

interface IOrderSearchFormState {
  expand: boolean;
  /** 查询loading */
  searchLoading: boolean;
}

class OrderSearchForm extends React.Component<IOrderSearchFormProps, IOrderSearchFormState> {
  state: IOrderSearchFormState = {
    expand: true,
    searchLoading: false,
  };
  /** 导出参数(上一次调用查询接口时的参数) */
  private queryParam: IOrderSearch | undefined;

  /** form的缓存 */
  private cachedFormValues?: any;

  /** 静态缓存，记住上次filter内容 */
  static sLastQuery?: any;

  constructor(props: IOrderSearchFormProps) {
    super(props);
  }

  /**
   * 获取上一次调用查询接口时的参数
   * 供父级组件调用
   */
  getQueryParam(): IOrderSearch | undefined {
    return this.queryParam;
  }

  toggle = () => {
    const { expand } = this.state;
    this.setState({ expand: !expand });
  };

  componentDidMount() {
    this.props.onRef(this);

    // 还原上次搜索条件
    if (OrderSearchForm.sLastQuery) {
      this.props.form.setFieldsValue(OrderSearchForm.sLastQuery);
      this.cachedFormValues = OrderSearchForm.sLastQuery;
    }
  }

  componentWillUnmount() {
    this.setState = () => {
      return;
    };
  }

  handleSearch = (e: React.SyntheticEvent) => {
    e.preventDefault();
    const { pageSize } = this.props;
    this.doSearch({
      limit: pageSize || 20,
      offset: 0,
    });
  };

  doSearch = (param: IOrderSearchParam) => {
    const { status } = this.props;
    this.setState({
      searchLoading: true,
    });
    this.props.form.validateFields(async (err, values) => {
      if (!err) {
        this.cachedFormValues = Object.assign({}, values);

        try {
          let startDate: string | undefined;
          let endDate: string | undefined;
          if (!!values.validityDate && values.validityDate.length > 1) {
            startDate = values.validityDate[0].format('YYYY-MM-DD 0:00:00');
            endDate = values.validityDate[1].format('YYYY-MM-DD 24:00:00');
            // startDate = values.validityDate[0].format('YYYY-MM-DD');
            // endDate = values.validityDate[1].format('YYYY-MM-DD');
          }
          const payStatus = orderStateToPayStatus(status);
          // 支付方式(合并查询通联[支付宝|微信])
          let paymentTypes: ORDER_PAYMENT[] | undefined;
          if (!!values.payment_type__in) {
            paymentTypes = [values.payment_type__in];
            if (values.payment_type__in === PAYMENT_ALIPY) {
              // 支付宝 => 合并查询通联(支付宝)
              paymentTypes.push(PAYMENT_ALIPAY_ORG);
            } else if (values.payment_type__in === PAYMENT_WECHAT) {
              // 微信 => 合并查询通联(微信)
              paymentTypes.push(PAYMENT_WECHAT_ORG);
            }
          }

          const query: IOrderSearch = {
            limit: param.limit,
            offset: param.offset,
            pay_status__in: payStatus.join(','),
            category: values.order_category,
            // category: this.props.category,
            store: values.drugStore,
            doctor_name: values.doctor_name,
            receiver_name: values.receiver_name,
            payment_type__in: paymentTypes,
            created_time__gte: startDate,
            created_time__lt: endDate,
            out_trade_no: values.out_trade_no,
            // invoice_issue_status_wrapper: values.order_invoice_status,
            ordering: param.ordering,
            take_medicine_code: values.take_medicine_code,
          };
          if (values.order_category === CATEGORY_PRESCRIPTION) {
            // 订单类型为处方订单时 使用 exclude_category=Outpatient 查询
            delete query.category;
            query.exclude_category = CATEGORY_ORIGINAL;
          }
          // 临时保存查询参数的快照
          const queryParam = { ...query };
          const result = await OrderService.getOrders(query);
          if (!!result) {
            this.queryParam = queryParam;

            // 从后端批量获取物流信息，并设置到对应的药品/处方订单的express字段上
            const orderIds = result.results.map(order => order.id);
            const expressOrders = await MallService.getExpressByOrders(orderIds);
            const expressMap = new Map<string, ILogistic>();
            expressOrders.forEach(e => e.goods.forEach(g => expressMap.set(g.order_no, e)));
            result.results.forEach(order => order.express = expressMap.get(order.id));

            this.props.cbSearch(status, result);
          }
        } catch (error) {
          if (!!error.response) {
            message.error(JSON.stringify(error.response.data));
          } else {
            message.error(I18N.get('err_search_failure'));
          }
        }
      }
      this.setState({ searchLoading: false });
    });
  };

  saveCache = (action?: 'clear') => {
    if (action === 'clear') {
      OrderSearchForm.sLastQuery = undefined;
    } else {
      if (this.cachedFormValues) {
        OrderSearchForm.sLastQuery = this.cachedFormValues;
      }
    }
  }

  handleReset = () => {
    this.props.form.resetFields();
  };

  getDrugStore = (): JSX.Element[] => {
    const options: JSX.Element[] = [];
    for (const option of this.props.drugStores) {
      options.push(
        <Select.Option key={String(option.id)} value={option.id}>
          {option.name}
        </Select.Option>
      );
    }
    return options;
  };

  getOrderInvoiceStatusOptions = (): JSX.Element[] => {
    const options: JSX.Element[] = [];
    for (const option of InvoiceStatusList) {
      options.push(
        <Select.Option key={option.key} value={option.value}>
          {I18N.get(option.key, false)}
        </Select.Option>
      );
    }
    return options;
  };

  getPaymentMethod = (): JSX.Element[] => {
    const options: JSX.Element[] = [];
    for (const option of OrderPayment) {
      if (option.is_show) {
        options.push(
          <Select.Option key={option.key} value={option.value}>
            {I18N.get(option.key)}
          </Select.Option>
        );
      }
    }
    return options;
  };

  render() {
    const { searchLoading } = this.state;
    const { isAgent } = this.props;
    const { getFieldDecorator } = this.props.form;

    return (
      <Form className="mdd-search-form" onSubmit={this.handleSearch}>
        <Row type="flex" align="middle">
          <div className="auto-flex-item">
            <Row gutter={24}>
              <Col span={8}>
                <Form.Item label={I18N.get('order_validity_date')} style={{ marginBottom: 0 }}>
                  {getFieldDecorator(`validityDate`, {
                    rules: [{ type: 'array', message: `${I18N.get('common_choose')}${I18N.get('order_validity_date')}` }],
                  })(<RangePicker />)}
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label={I18N.get('order_payment')} style={{ marginBottom: 0 }}>
                  {getFieldDecorator(`payment_type__in`)(<Select placeholder={I18N.get('common_choose')}>{this.getPaymentMethod()}</Select>)}
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label={I18N.get('order_category')} style={{ marginBottom: 0 }}>
                  {getFieldDecorator(`order_category`)(
                    <Select placeholder={I18N.get('common_choose')}>
                      <Select.Option key={CATEGORY_ORIGINAL} value={CATEGORY_ORIGINAL}>
                        {I18N.get('nav_order_medicine')}
                      </Select.Option>
                      <Select.Option key={CATEGORY_PRESCRIPTION} value={CATEGORY_PRESCRIPTION}>
                        {I18N.get('nav_order_prescription')}
                      </Select.Option>
                    </Select>
                  )}
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={24} style={{ marginTop: 10 }}>
              {isAgent && (
                <Col span={8}>
                  <Form.Item label={I18N.get('nav_drugStore')} style={{ marginBottom: 0 }}>
                    {getFieldDecorator(`drugStore`)(
                      <Select key="nav_drugStore" placeholder={I18N.get('common_choose')}>
                        {this.getDrugStore()}
                      </Select>
                    )}
                  </Form.Item>
                </Col>
              )}
              <Col span={8}>
                  <Form.Item label={I18N.get('order_no')} style={{ marginBottom: 0 }}>
                    {getFieldDecorator(`out_trade_no`)(<Input key="order_no" placeholder={I18N.get('order_no')} />)}
                  </Form.Item>
                </Col>
              <Col span={8}>
                <Form.Item label={I18N.get('common_doctor')} style={{ marginBottom: 0 }}>
                  {getFieldDecorator(`doctor_name`)(<Input key="common_doctor" placeholder={I18N.get('common_doctor')} />)}
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label={I18N.get('order_receiver')} style={{ marginBottom: 0 }}>
                  {getFieldDecorator(`receiver_name`)(<Input key="order_receiver" placeholder={I18N.get('order_receiver')} />)}
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label={I18N.get('order_take_code')} style={{ marginBottom: 0 }}>
                  {getFieldDecorator(`take_medicine_code`)(<Input key="order_take_code" placeholder={I18N.get('order_take_code')} />)}
                </Form.Item>
              </Col>
            </Row>
            
          </div>
          <div className="fixed-flex-item flex-column" style={{ marginLeft: 30 }}>
            <Button type="primary" htmlType="submit" loading={searchLoading}>
              {I18N.get('common_search')}
            </Button>
            <Button style={{ marginTop: 16 }} onClick={this.handleReset}>
              {I18N.get('common_clear')}
            </Button>
          </div>
        </Row>
      </Form>
    );
  }
}

const OrderSearch = Form.create<IOrderSearchFormProps>()(OrderSearchForm);
export default OrderSearch;



// WEBPACK FOOTER //
// ./src/components/custom/OrderSearch.tsx